// PlatformHealth - /health. ADR-198 (DRAFT) · health-dashboard-spec.md · Lane AM build step 6 · G001 Lane AR DESIGN D6.
//
// Renders GET /api/health (schema health-api/2) and NOTHING else. It evaluates nothing: every state, colour, role,
// status line and headline comes from the server's port of the golden-vector rule; ../components/health/present.ts
// maps them to styles and can only downgrade an answer that contradicts itself to UNKNOWN, never upgrade one.
// Admin-only: the API answers 403 to anyone else and the page says so. Read-only: no control on this page acts on
// anything (the tiles, expanders and the "show retired" toggle only change what is drawn).
//
// Layout (DESIGN D6): the header with both timestamps · the counts line · the headline sentence · the state store
// line · the DATAFLOW plane (bands by layer, columns by source system, the rail, connectors) · the CONTROL PLANE (with
// the Watchers row and, when non-empty, the unplaced / invalid strip) · the collapsed Monitors registry · the retired
// toggle. Any fetch failure (network, timeout, 5xx, an answer that is not the contract) replaces the whole body with a
// full-page UNKNOWN banner - never an empty page, never an earlier answer that can no longer be vouched for. An answer
// that cannot be DRAWN is treated the same way: the header, the tab title and "last successful fetch" only ever vouch
// for an answer that was actually drawn.
import { Component, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import "../components/health/health.css";
import type { HealthGridAsset, HealthResponse } from "../components/health/types";
import { FETCH_TIMEOUT_MS, POLL_MS, parseHealthResponse, plural, readBuildStamp, titleText } from "../components/health/present";
import { buildGridCtx } from "../components/health/ctx";
import { columnsFromGrid, columnsFromLabels, layoutPlane } from "../components/health/grid";
import { HeaderA } from "../components/health/HeaderA";
import { CountsLine, Headline } from "../components/health/CountsLine";
import { StoreLine } from "../components/health/StoreLine";
import { GridPlane } from "../components/health/GridPlane";
import { UnplacedStrip } from "../components/health/UnplacedStrip";
import { MonitorsRegistry } from "../components/health/MonitorsRegistry";
import { AssetDrawer, PanelContext, type PanelApi } from "../components/health/AssetPanel";
import {
  Forbidden,
  Loading,
  NotSignedIn,
  NothingKnown,
  StaleBanner,
  StoreUnreadableBanner
} from "../components/health/StatusScreens";

type Fetched =
  | { kind: "ok"; data: HealthResponse }
  | { kind: "unauthenticated" }
  | { kind: "forbidden" }
  | { kind: "failed"; why: string };

type Outcome = { kind: "loading" } | { kind: "ok"; data: HealthResponse; at: number } | Exclude<Fetched, { kind: "ok" }>;

interface PollState {
  outcome: Outcome;
  lastSuccessMs: number | null;
  lastAttemptMs: number | null;
}

/** The last answer that was fetched AND drawn (a render crash never counts as a success). */
interface Drawn {
  at: number;
  title: string;
  prevAt: number | null;
}

const TITLE = "Platform health";
/** R5: the dataflow grid's min-width - 52rem (832px) fits the 842px its plane has at a 900px viewport (was 62rem = 992px) */
const DATAFLOW_MIN_WIDTH_REM = 52;
const UNKNOWN_TITLE = `UNKNOWN · ${TITLE}`;

async function fetchHealth(signal: AbortSignal): Promise<Fetched> {
  let resp: Response;
  try {
    resp = await fetch("/api/health", {
      credentials: "include",
      cache: "no-store",
      // SWA answers an unauthenticated API call with a 302 to the sign-in page; following it cross-origin would
      // surface as an opaque network error. "manual" turns it into a readable opaqueredirect instead.
      redirect: "manual",
      headers: { Accept: "application/json" },
      signal
    });
  } catch {
    return {
      kind: "failed",
      why: signal.aborted
        ? `the health API did not answer within ${FETCH_TIMEOUT_MS / 1000} seconds`
        : "the health API could not be reached (network error)"
    };
  }
  if (resp.type === "opaqueredirect" || resp.status === 401) return { kind: "unauthenticated" };
  if (resp.status === 403) return { kind: "forbidden" };
  if (!resp.ok) return { kind: "failed", why: `the health API answered HTTP ${resp.status}` };
  let body: unknown;
  try {
    body = await resp.json();
  } catch {
    return { kind: "failed", why: "the health API answered with something that is not JSON" };
  }
  const parsed = parseHealthResponse(body);
  return parsed.ok ? { kind: "ok", data: parsed.data } : { kind: "failed", why: `the health API's answer does not match the contract: ${parsed.why}` };
}

/** Fetch now, then every 5 minutes, and again when a hidden tab becomes visible after a missed poll. */
function useHealthPoll(): PollState {
  const [state, setState] = useState<PollState>({ outcome: { kind: "loading" }, lastSuccessMs: null, lastAttemptMs: null });
  const inFlight = useRef(false);
  const lastAttempt = useRef(0);

  const run = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    lastAttempt.current = Date.now();
    const ctl = new AbortController();
    const timer = window.setTimeout(() => ctl.abort(), FETCH_TIMEOUT_MS);
    let fetched: Fetched;
    try {
      fetched = await fetchHealth(ctl.signal);
    } catch {
      fetched = { kind: "failed", why: "the page could not complete the request" };
    } finally {
      window.clearTimeout(timer);
      inFlight.current = false;
    }
    const at = Date.now();
    const outcome: Outcome = fetched.kind === "ok" ? { ...fetched, at } : fetched;
    setState(prev => ({ outcome, lastAttemptMs: at, lastSuccessMs: outcome.kind === "ok" ? at : prev.lastSuccessMs }));
  }, []);

  useEffect(() => {
    void run();
    const interval = window.setInterval(() => void run(), POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - lastAttempt.current >= POLL_MS) void run();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [run]);

  return state;
}

function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(id);
  }, [everyMs]);
  return now;
}

/** The retired toggle (DESIGN D6): retired assets are hidden by default; showing them draws them in their grid place,
 *  greyed. Their panels - and the checks that landed on them - are always in the page. */
function RetiredToggle({ hidden, on, onChange }: { hidden: HealthGridAsset[]; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <p className="ph-retired-toggle" data-testid="retired-toggle">
      <label className="ph-toggle">
        <input type="checkbox" checked={on} disabled={hidden.length === 0} onChange={e => onChange(e.target.checked)} /> show retired (
        {hidden.length})
      </label>{" "}
      <span className="ph-hint">
        {hidden.length === 0
          ? "no retired asset in this answer"
          : `${plural(hidden.length, "retired asset")} - deliberately superseded, so their stillness is correct: ${hidden.map(a => `${a.label} (${a.env})`).join(", ")}`}
      </span>
    </p>
  );
}

/** Everything drawn from one answer, top to bottom (DESIGN D6) - and the one details drawer. Exported so the render tests
 *  draw exactly what the page draws. */
export function HealthView({ data, now, initialShowRetired = false }: { data: HealthResponse; now: number; initialShowRetired?: boolean }) {
  const ctx = useMemo(() => buildGridCtx(data), [data]);
  const [showRetired, setShowRetired] = useState(initialShowRetired);
  const [openId, setOpenId] = useState<string | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const panel = useMemo<PanelApi>(
    () => ({
      openId,
      open: (id, from) => {
        opener.current = from ?? null;
        setOpenId(id);
      },
      close: () => {
        setOpenId(null);
        opener.current?.focus();
      }
    }),
    [openId]
  );
  const g = data.grid;
  const hidden = g.assets.filter(a => a.hidden);
  const shown = (plane: string) => g.assets.filter(a => a.plane === plane && (showRetired || !a.hidden));
  const df = useMemo(
    () =>
      layoutPlane(
        g.dataflow.bands,
        columnsFromGrid(g.dataflow.bands, g.dataflow.columns),
        shown("dataflow"),
        g.dataflow.rail ? { between: g.dataflow.rail.between, assets: [...g.dataflow.rail.from, ...g.dataflow.rail.to.map(t => t.asset)] } : null
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, showRetired]
  );
  const cp = useMemo(
    () => layoutPlane(g.control.bands, columnsFromLabels(g.control.columns), shown("control")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, showRetired]
  );
  // an asset on neither plane cannot be positioned either (the parser admits only the two, but never drop one)
  const offPlane = g.assets.filter(a => a.plane !== "dataflow" && a.plane !== "control").map(asset => ({ asset, why: `its plane '${String(asset.plane)}' is not drawn` }));
  // R5 (the round-5 hard gate, agent-14's X9): the dataflow grid's floor fits the 842px its plane has at a 900px viewport, so
  // every problem / unknown tile is in view there without scrolling sideways; below that the plane still scrolls (with its cue)
  return (
    <PanelContext.Provider value={panel}>
      <StaleBanner evaluatedAt={data.evaluated_at_utc} now={now} />
      {!data.store.read_ok ? <StoreUnreadableBanner error={data.store.read_error} /> : null}
      <CountsLine data={data} ctx={ctx} />
      <Headline data={data} ctx={ctx} />
      <StoreLine data={data} />
      <GridPlane
        plane="dataflow"
        title="DATAFLOW"
        hint="bands by layer, columns by source system · click a tile for its details"
        layout={df}
        links={g.links}
        rail={g.dataflow.rail}
        ctx={ctx}
        minWidthRem={DATAFLOW_MIN_WIDTH_REM}
      />
      <GridPlane
        plane="control"
        title="CONTROL PLANE"
        hint="repositories, deploys and what is deployed - a broken deploy leaves every data tile green"
        layout={cp}
        links={g.links}
        ctx={ctx}
        minWidthRem={46}
      >
        <UnplacedStrip ctx={ctx} unplaceable={[...df.unplaceable, ...cp.unplaceable, ...offPlane]} />
      </GridPlane>
      <MonitorsRegistry data={data} />
      <RetiredToggle hidden={hidden} on={showRetired} onChange={setShowRetired} />
      <AssetDrawer ctx={ctx} />
    </PanelContext.Provider>
  );
}

/** If drawing the answer throws, show the full-page UNKNOWN banner - never a blank or half-drawn page - and tell
 *  the page, so the header and tab title stop vouching for the answer that could not be drawn. */
class RenderGuard extends Component<
  { children: ReactNode; lastSuccessMs: number | null; now: number; onCrash: () => void },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onCrash();
  }

  render() {
    if (this.state.failed) {
      return (
        <NothingKnown
          why="the page could not draw the health API's answer (a rendering error), so it shows none of it rather than part of it"
          lastSuccessMs={this.props.lastSuccessMs}
          now={this.props.now}
        />
      );
    }
    return this.props.children;
  }
}

function HealthBody({
  data,
  at,
  now,
  onDrawn
}: {
  data: HealthResponse;
  at: number;
  now: number;
  onDrawn: (at: number, title: string) => void;
}) {
  const title = titleText(data, TITLE);
  // Effects run only for a subtree that committed: an answer that throws while drawing never reports itself drawn.
  useEffect(() => onDrawn(at, title), [at, title, onDrawn]);
  return <HealthView data={data} now={now} />;
}

export default function PlatformHealth() {
  const poll = useHealthPoll();
  const tick = useNow(30_000);
  const now = Math.max(tick, poll.lastAttemptMs ?? 0);
  const stamp = useMemo(() => readBuildStamp(), []);
  const [drawn, setDrawn] = useState<Drawn | null>(null);
  const [crashedAt, setCrashedAt] = useState<number | null>(null);
  const o = poll.outcome;
  const crashed = o.kind === "ok" && crashedAt === o.at;

  const onDrawn = useCallback((at: number, title: string) => {
    setDrawn(d => (d !== null && d.at === at ? (d.title === title ? d : { ...d, title }) : { at, title, prevAt: d?.at ?? null }));
  }, []);
  const onCrash = useCallback((at: number) => setCrashedAt(at), []);
  // "last successful fetch" = the last answer fetched AND drawn; an answer that crashed after drawing drops out.
  const lastGoodMs = drawn === null ? null : crashedAt !== null && drawn.at === crashedAt ? drawn.prevAt : drawn.at;

  useEffect(() => {
    const previous = document.title;
    return () => {
      document.title = previous;
    };
  }, []);
  useEffect(() => {
    document.title =
      o.kind === "loading" ? TITLE : o.kind === "ok" && !crashed ? (drawn !== null && drawn.at === o.at ? drawn.title : TITLE) : UNKNOWN_TITLE;
  }, [o, crashed, drawn]);

  const shown = o.kind === "ok" && !crashed ? o.data : null;
  const asOfNote = crashed
    ? "withdrawn - the answer could not be drawn, so none of it is vouched for"
    : poll.lastSuccessMs !== null
      ? "withdrawn - the last answer can no longer be vouched for"
      : "no answer yet";

  return (
    <div className="ph" data-testid="platform-health">
      <HeaderA data={shown} asOfNote={asOfNote} stamp={stamp} now={now} lastSuccessMs={lastGoodMs} />
      {o.kind === "loading" ? <Loading /> : null}
      {o.kind === "unauthenticated" ? <NotSignedIn /> : null}
      {o.kind === "forbidden" ? <Forbidden /> : null}
      {o.kind === "failed" ? <NothingKnown why={o.why} lastSuccessMs={lastGoodMs} now={now} /> : null}
      {o.kind === "ok" ? (
        <RenderGuard key={o.at} lastSuccessMs={lastGoodMs} now={now} onCrash={() => onCrash(o.at)}>
          <HealthBody data={o.data} at={o.at} now={now} onDrawn={onDrawn} />
        </RenderGuard>
      ) : null}
    </div>
  );
}
